import { useState } from 'react';
import useSWR, { mutate } from 'swr';
import { toast } from 'sonner';
import { get, post, put, ApiError } from '@/lib/api';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ProductConfigDrawer } from '@/components/domain/ProductConfigDrawer';
import { CATEGORIES, type ProductConfig } from '@/lib/product-config';

type Payload = { products: ProductConfig[] };
type FetchImagesResult = {
  success: boolean;
  updated: number;
  queried: number;
  skipped: { id: string; reason: string }[];
  cacheRefreshed: boolean;
};

// Garment photo (views.front) — supplier CDN / storage URL, rendered directly.
function ProductThumb({ p }: { p: ProductConfig }) {
  const src = p.views?.front?.imageUrl || p.views?.front?.imageDataUrl;
  if (!src) return <span className="h-9 w-9 shrink-0 rounded-[6px] bg-bg" aria-hidden />;
  return <img src={src} alt="" loading="lazy" className="h-9 w-9 shrink-0 rounded-[6px] bg-white object-contain" />;
}

// Image-workflow chip (mirrors product-config-admin's card badges): AI ✓ =
// approved standardized photo live; AI review = candidate awaiting review in
// pc-admin; No image = nothing in views.front yet (fetch-images target).
function imageChip(p: ProductConfig) {
  const front = p.views?.front;
  if (front?.aiStatus === 'approved') return <Badge className="bg-success/10 text-success">AI ✓</Badge>;
  if (front?.aiStatus === 'generated') return <Badge className="bg-warning/10 text-warning">AI review</Badge>;
  if (!front?.imageUrl) return <Badge className="bg-bg text-subtle">No image</Badge>;
  return null;
}

// Drawstring overlay chip — surfaces whether a per-(style,color) overlay is
// authored and whether it has drifted vs the current base flat-lay (overlayStale
// flag mirrored back from teamstore-mockups on render-time baseSig mismatch).
function overlayChip(p: ProductConfig) {
  const front = p.views?.front;
  if (!front?.overlayImage) return null;
  if (front.overlayStale) {
    return (
      <Badge className="bg-warning/10 text-warning" title={front.overlayStaleReason || 'overlay misaligned with base'}>
        Overlay ⚠
      </Badge>
    );
  }
  return <Badge className="bg-success/10 text-success">Overlay ✓</Badge>;
}

export default function Products() {
  const [q, setQ] = useState('');
  const [openProduct, setOpenProduct] = useState<ProductConfig | null>(null);
  const [drawerMode, setDrawerMode] = useState<'view' | 'create'>('view');
  const [fCat, setFCat] = useState('all');
  const [fBrand, setFBrand] = useState('all');
  const [fApproval, setFApproval] = useState<'all' | 'approved' | 'pending'>('all');
  const [fetchingImages, setFetchingImages] = useState(false);
  // Reorder mode: a local working copy ordered by sortOrder; null = off.
  const [orderList, setOrderList] = useState<ProductConfig[] | null>(null);
  const [savingOrder, setSavingOrder] = useState(false);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const { data, isLoading } = useSWR<Payload>('/api/products?limit=500', get);
  const brands = Array.from(new Set((data?.products || []).map((p) => p.brandPartner).filter(Boolean))).sort() as string[];

  const filtered = (data?.products || []).filter((p) => {
    if (fCat !== 'all' && p.category !== fCat) return false;
    if (fBrand !== 'all' && p.brandPartner !== fBrand) return false;
    if (fApproval === 'approved' && !p.approved) return false;
    if (fApproval === 'pending' && p.approved) return false;
    if (q.trim()) {
      const needle = q.toLowerCase();
      const hay = `${p.displayName || ''} ${p.productTitle || ''} ${p.brandPartner || ''} ${p.id}`.toLowerCase();
      if (!hay.includes(needle)) return false;
    }
    return true;
  });

  async function fetchMissingImages() {
    setFetchingImages(true);
    try {
      const r = await post<FetchImagesResult>('/api/products/fetch-images', {});
      const reasons = r.skipped.reduce<Record<string, number>>((acc, s) => {
        acc[s.reason] = (acc[s.reason] || 0) + 1;
        return acc;
      }, {});
      const reasonText = Object.entries(reasons).map(([k, n]) => `${k.replace(/_/g, ' ')} ${n}`).join(', ');
      toast.success(`Filled images on ${r.updated} product${r.updated === 1 ? '' : 's'} (${r.queried} styles queried)${reasonText ? ` — skipped: ${reasonText}` : ''}`);
      await mutate('/api/products?limit=500');
    } catch (e) {
      const body = (e as ApiError).body as { message?: string; error?: string } | null;
      toast.error(body?.message || body?.error || 'Image fetch failed');
    } finally {
      setFetchingImages(false);
    }
  }

  function enterReorder() {
    const list = [...(data?.products || [])].sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
    setOrderList(list);
  }

  function move(from: number, to: number) {
    setOrderList((list) => {
      if (!list || to < 0 || to >= list.length) return list;
      const next = [...list];
      const [item] = next.splice(from, 1);
      next.splice(to, 0, item);
      return next;
    });
  }

  async function saveOrder() {
    if (!orderList) return;
    setSavingOrder(true);
    try {
      const r = await put<{ updated: number; cacheRefreshed: boolean }>('/api/products-sort', {
        order: orderList.map((p, i) => ({ id: p.id, sortOrder: i + 1 })),
      });
      toast.success(`Sort order saved for ${r.updated} products${r.cacheRefreshed ? '' : ' (cache refresh pending — applies within a minute)'}`);
      setOrderList(null);
      await mutate('/api/products?limit=500');
    } catch (e) {
      const body = (e as ApiError).body as { message?: string; error?: string } | null;
      toast.error(body?.message || body?.error || 'Save failed');
    } finally {
      setSavingOrder(false);
    }
  }

  return (
    <div className="px-7 py-6">
      <div className="mb-3 flex items-start justify-between gap-3">
        <div>
          <h1 className="text-[20px] font-semibold tracking-tighter">Products</h1>
          <p className="text-[12px] text-muted">Product configurations across all suppliers · {data?.products.length ?? 0} total</p>
        </div>
        <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
          {orderList === null ? (
            <>
              <Button size="sm" onClick={() => { setDrawerMode('create'); setOpenProduct({ id: '', baseProduct: {} } as ProductConfig); }}>
                New product
              </Button>
              <Button size="sm" variant="secondary" disabled={fetchingImages || isLoading} onClick={fetchMissingImages} title="Pull supplier photos into empty front/back/side image slots. Never overwrites existing or AI-approved images.">
                {fetchingImages ? 'Fetching…' : 'Fetch missing images'}
              </Button>
              <Button size="sm" variant="secondary" disabled={isLoading} onClick={enterReorder}>
                Reorder
              </Button>
              <a
                href="/api/products-export"
                className="rounded-[8px] border border-black/[0.08] bg-surface px-3 py-1.5 text-[11.5px] font-medium text-text shadow-[0_1px_1.5px_rgba(0,0,0,0.04)] hover:bg-[#f7f7f9]"
              >
                Export CSV
              </a>
            </>
          ) : (
            <>
              <Button size="sm" disabled={savingOrder} onClick={saveOrder}>{savingOrder ? 'Saving…' : 'Save order'}</Button>
              <Button size="sm" variant="ghost" disabled={savingOrder} onClick={() => setOrderList(null)}>Cancel</Button>
            </>
          )}
        </div>
      </div>

      {orderList === null && (
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, brand, or ID…" className="max-w-[280px]" />
          <select className="rounded-md border border-border bg-bg px-2 py-1.5 text-[12px]" value={fCat} onChange={(e) => setFCat(e.target.value)}>
            <option value="all">All categories</option>
            {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <select className="rounded-md border border-border bg-bg px-2 py-1.5 text-[12px]" value={fBrand} onChange={(e) => setFBrand(e.target.value)}>
            <option value="all">All brands</option>
            {brands.map((b) => <option key={b} value={b}>{b}</option>)}
          </select>
          <select className="rounded-md border border-border bg-bg px-2 py-1.5 text-[12px]" value={fApproval} onChange={(e) => setFApproval(e.target.value as 'all' | 'approved' | 'pending')}>
            <option value="all">All</option>
            <option value="approved">Approved</option>
            <option value="pending">Pending</option>
          </select>
        </div>
      )}

      {orderList !== null ? (
        <div className="rounded-md bg-surface shadow-card">
          <div className="border-b border-divider px-3 py-2 text-[11px] text-muted">
            Drag rows (or use the arrows) to set the storefront product order, then Save. All {orderList.length} products are listed.
          </div>
          {orderList.map((p, i) => (
            <div
              key={p.id}
              draggable
              onDragStart={() => setDragIndex(i)}
              onDragOver={(e) => { e.preventDefault(); }}
              onDrop={(e) => { e.preventDefault(); if (dragIndex !== null && dragIndex !== i) move(dragIndex, i); setDragIndex(null); }}
              onDragEnd={() => setDragIndex(null)}
              className={`flex w-full items-center gap-3 border-b border-divider px-3 py-1.5 last:border-b-0 ${dragIndex === i ? 'opacity-50' : ''}`}
            >
              <span className="cursor-grab text-subtle" title="Drag to reorder">⠿</span>
              <span className="w-8 shrink-0 text-right font-mono text-[10px] text-subtle">{i + 1}</span>
              <span className="flex-1 truncate text-[12px]">
                {p.displayName || p.productTitle || p.id}
                {p.active === false && <Badge className="ml-2 bg-bg text-muted">inactive</Badge>}
              </span>
              <span className="font-mono text-[10px] text-subtle">{p.id}</span>
              <span className="flex shrink-0 gap-1">
                <Button size="sm" variant="ghost" disabled={i === 0} onClick={() => move(i, i - 1)} aria-label="Move up">↑</Button>
                <Button size="sm" variant="ghost" disabled={i === orderList.length - 1} onClick={() => move(i, i + 1)} aria-label="Move down">↓</Button>
              </span>
            </div>
          ))}
        </div>
      ) : (
        <div className="rounded-md bg-surface shadow-card">
          {isLoading ? (
            <div className="space-y-2 p-3">
              <Skeleton className="h-7 w-full" />
              <Skeleton className="h-7 w-full" />
              <Skeleton className="h-7 w-full" />
            </div>
          ) : filtered.length ? (
            filtered.slice(0, 200).map((p) => (
              <button
                key={p.id}
                onClick={() => setOpenProduct(p)}
                className="flex w-full items-center gap-3 border-b border-divider px-3 py-2 text-left last:border-b-0 hover:bg-bg"
              >
                <ProductThumb p={p} />
                <span className="font-mono text-[10px] text-subtle w-24 shrink-0 truncate">{p.id}</span>
                <span className="flex-1 truncate text-[12px]">
                  {p.displayName || p.productTitle || 'Unnamed'}
                  {p.gender && <span className="ml-2 text-muted">· {String(p.gender)}</span>}
                </span>
                {p.active === false && <Badge className="bg-bg text-muted">inactive</Badge>}
                {imageChip(p)}
                {overlayChip(p)}
                {p.brandPartner && <Badge className="bg-bg text-muted">{p.brandPartner}</Badge>}
                {(p.decoration?.method || p.decorationMethod) && <Badge className="bg-primary/10 text-primary">{p.decoration?.method || p.decorationMethod}</Badge>}
              </button>
            ))
          ) : (
            <div className="p-8 text-center text-[12px] text-muted">No products match "{q}".</div>
          )}
          {filtered.length > 200 && (
            <div className="border-t border-divider px-3 py-2 text-center text-[11px] text-muted">
              Showing first 200 of {filtered.length}. Refine search to narrow.
            </div>
          )}
        </div>
      )}

      <ProductConfigDrawer
        open={!!openProduct}
        mode={drawerMode}
        onClose={() => { setOpenProduct(null); setDrawerMode('view'); }}
        product={openProduct}
        onDuplicate={(seed) => { setDrawerMode('create'); setOpenProduct(seed); }}
      />
    </div>
  );
}
